import { getCurrencySymbol } from 'src/utils/getCurrencySymbol'
import { displayAmountClean } from 'src/utils/displayAmountClean'
import type { FreeBetGroup } from './groupFreeBets'

export interface FreeBetBannerCopy {
  title: string
  subtitle: string
  reward: string
  count: number
}

// i18next treats `count` as a pluralization selector, so the interpolation
// variable is `num`. Full-phrase keys (not word-by-word) keep the "free bet"
// noun translatable as one unit — important for en-soc (sweepstakes), where it
// must read "free play" instead of "free bet".
type Translate = (key: string, params?: Record<string, unknown>) => string

/**
 * The text for an aggregated free-bet grant banner, e.g.
 * title "10 free bets on Plinko", subtitle "$5 each", reward "$5".
 * Pure (no JSX) so it can be unit-tested without the UI layer.
 */
export function freeBetBannerCopy(
  group: FreeBetGroup,
  t: Translate,
  gameName?: string
): FreeBetBannerCopy {
  const reward = `${getCurrencySymbol(group.currency)}${displayAmountClean(group.amount)}`
  const isMulti = group.count > 1
  // Name a game only when the grant is game-specific (empty applicableGames = all games).
  const game = group.applicableGames.length > 0 ? gameName : undefined

  let title: string
  if (isMulti) {
    title = game
      ? t('{{num}} free bets on {{game}}', { num: group.count, game })
      : t('{{num}} free bets', { num: group.count })
  } else {
    title = game ? t('Free bet on {{game}}', { game }) : t('Free bet')
  }

  return {
    title,
    subtitle: isMulti ? t('{{amount}} each', { amount: reward }) : t('Inventory'),
    reward,
    count: group.count
  }
}
